import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL, ApiClientError } from "./client";

export interface FileSummaryDto {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

async function parseJsonError(response: Response): Promise<never> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const payload = (await response.json()) as { error?: { message?: string } };
    throw new ApiClientError(response.status, {
      error: {
        code: "INTERNAL_ERROR",
        message: payload.error?.message ?? `Request failed with status ${response.status}`,
      },
    });
  }
  throw new ApiClientError(response.status, {
    error: {
      code: "INTERNAL_ERROR",
      message: `Request failed with status ${response.status}`,
    },
  });
}

export async function uploadFile(input: {
  file: File;
  entityType?: "expense" | "receipt";
  entityId?: string;
}): Promise<FileSummaryDto> {
  const formData = new FormData();
  formData.append("file", input.file);
  if (input.entityType) {
    formData.append("entityType", input.entityType);
  }
  if (input.entityId) {
    formData.append("entityId", input.entityId);
  }

  const response = await fetch(`${API_BASE_URL}/files`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    return parseJsonError(response);
  }

  const payload = (await response.json()) as { file: FileSummaryDto };
  return payload.file;
}

export function fileDownloadUrl(fileId: string): string {
  return `${API_BASE_URL}/files/${fileId}`;
}

export function useUploadFileMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadFile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["transactions"] });
      await queryClient.invalidateQueries({ queryKey: ["overview", "attention"] });
    },
  });
}

export async function deleteFile(fileId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/files/${fileId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) {
    return parseJsonError(response);
  }
}

export function useDeleteFileMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["transactions"] });
      await queryClient.invalidateQueries({ queryKey: ["expense"] });
      await queryClient.invalidateQueries({ queryKey: ["overview", "attention"] });
    },
  });
}
